import { taskPrioritySchema, taskStatusSchema } from '@amore/shared'
import { z } from 'zod'
import type { PlanningTask } from '@/features/tasks/api'

export const taskFilterSchema = z.enum(['all', 'open', 'done', 'overdue', 'upcoming'])
export type TaskFilter = z.infer<typeof taskFilterSchema>

export function filterTasks(
  tasks: readonly PlanningTask[],
  filter: TaskFilter,
  today = new Date(),
) {
  const range = taskDateRange(today)
  return tasks.filter((task) => {
    const done = task.status === 'done'
    switch (filter) {
      case 'done':
        return done
      case 'open':
        return !done
      case 'overdue':
        return !done && Boolean(task.dueDate && task.dueDate < range.today)
      case 'upcoming':
        return (
          !done &&
          Boolean(
            task.dueDate && task.dueDate >= range.today && task.dueDate <= range.nextFourteenDays,
          )
        )
      default:
        return true
    }
  })
}

const priorityRank = { high: 0, normal: 1, low: 2 } as const

export type TaskGroups = {
  priority: readonly PlanningTask[]
  current: readonly PlanningTask[]
  done: readonly PlanningTask[]
}

export function toLocalDateKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function taskDateRange(today: Date) {
  const nextFourteenDays = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  nextFourteenDays.setDate(nextFourteenDays.getDate() + 14)
  return { today: toLocalDateKey(today), nextFourteenDays: toLocalDateKey(nextFourteenDays) }
}

export function taskSummary(tasks: readonly PlanningTask[], today = new Date()) {
  const { today: todayKey, nextFourteenDays } = taskDateRange(today)
  const open = tasks.filter((task) => taskStatusSchema.catch('todo').parse(task.status) !== 'done')
  return {
    attention: open.filter((task) => isPriorityTask(task, { today: todayKey, nextFourteenDays }))
      .length,
    open: open.length,
    done: tasks.length - open.length,
    overdue: open.filter((task) => task.dueDate && task.dueDate < todayKey).length,
    upcoming: open.filter(
      (task) => task.dueDate && task.dueDate >= todayKey && task.dueDate <= nextFourteenDays,
    ).length,
  }
}

export function selectTaskGroups(tasks: readonly PlanningTask[], today = new Date()) {
  const range = taskDateRange(today)
  const open = tasks.filter((task) => taskStatusSchema.catch('todo').parse(task.status) !== 'done')
  return {
    priority: open
      .filter((task) => isPriorityTask(task, range))
      .toSorted((left, right) => comparePriorityTasks(left, right, range)),
    current: open.filter((task) => !isPriorityTask(task, range)).toSorted(compareCurrentTasks),
    done: tasks
      .filter((task) => taskStatusSchema.catch('todo').parse(task.status) === 'done')
      .toSorted(compareCompletedTasks),
  }
}

function isPriorityTask(task: PlanningTask, range: ReturnType<typeof taskDateRange>) {
  return (
    taskPrioritySchema.catch('normal').parse(task.priority) === 'high' ||
    Boolean(task.dueDate && task.dueDate <= range.nextFourteenDays)
  )
}

function comparePriorityTasks(
  left: PlanningTask,
  right: PlanningTask,
  range: ReturnType<typeof taskDateRange>,
) {
  const bucketDifference = priorityBucket(left, range) - priorityBucket(right, range)
  if (bucketDifference !== 0) return bucketDifference
  const priorityDifference = taskPriority(left.priority) - taskPriority(right.priority)
  if (priorityDifference !== 0) return priorityDifference
  const dateDifference = compareDueDates(left.dueDate, right.dueDate)
  if (dateDifference !== 0) return dateDifference
  const createdDifference = left.createdAt.localeCompare(right.createdAt)
  return createdDifference || left.id.localeCompare(right.id)
}

function priorityBucket(task: PlanningTask, range: ReturnType<typeof taskDateRange>) {
  if (task.dueDate && task.dueDate < range.today) return 0
  if (task.dueDate === range.today) return 1
  if (task.dueDate && task.dueDate <= range.nextFourteenDays) return 2
  return 3
}

function compareCurrentTasks(left: PlanningTask, right: PlanningTask) {
  const dateDifference = compareDueDates(left.dueDate, right.dueDate)
  if (dateDifference !== 0) return dateDifference
  const priorityDifference = taskPriority(left.priority) - taskPriority(right.priority)
  if (priorityDifference !== 0) return priorityDifference
  const createdDifference = left.createdAt.localeCompare(right.createdAt)
  return createdDifference || left.id.localeCompare(right.id)
}

function compareCompletedTasks(left: PlanningTask, right: PlanningTask) {
  const updatedDifference = right.updatedAt.localeCompare(left.updatedAt)
  return updatedDifference || left.id.localeCompare(right.id)
}

function taskPriority(priority: string) {
  return priorityRank[taskPrioritySchema.catch('normal').parse(priority)]
}

function compareDueDates(left: string | null, right: string | null) {
  if (left === right) return 0
  if (left === null) return 1
  if (right === null) return -1
  return left.localeCompare(right)
}
